/**
 * Pure helpers for the two FOLIAR operator screens (F5). No React, no network,
 * no i18n — the same shape as soilOperator.ts / foliarRosterSummary.ts.
 *
 * The banner state and the elapsed-minutes clock are IMPORTED from
 * soilOperator.ts, never re-implemented. Neither contains anything soil-specific,
 * and a second copy is the shape that has already bitten this initiative twice
 * (F1 paid down a duplicated roundState classifier; F2 watched it come back and
 * diverge on a reachable case). The case that would diverge here is
 * `online + queued`, which is precisely the state U5's ruling exists to protect:
 * a failed drain while ONLINE must not claim "Offline".
 */
import { resolveSyncBanner, elapsedMinutes } from './soilOperator';
import type { SyncBannerState } from './soilOperator';
import type { NutritionMixPreviewLine } from '../../services/nutritionSessionService';
import { formatQuantity } from '../../utils/formatQuantity';

// `export type`, not a bare re-export: CRA sets isolatedModules, under which a
// type re-exported as a value is a build failure jest cannot see.
export type { SyncBannerState };
export { resolveSyncBanner, elapsedMinutes };

/** The floor the backend has never enforced. See the plan's Ruling 2. */
export const MIN_TANKS = 0.1;

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;
const round3 = (n: number): number => Math.round((n + Number.EPSILON) * 1000) / 1000;

/**
 * One ± step on the tanks-applied hero control.
 *
 * Rounds after every step rather than accumulating: three unrounded 0.1 steps
 * from 0.2 give 0.5000000000000001, which the input then renders in full.
 * Clamps at MIN_TANKS. A 0 must never be emitted: since #506 the server
 * rejects it with TANK_COUNT_REQUIRED rather than booking the plan, so a 0
 * is now a dead end for the operator rather than a silent over-book — still
 * worth preventing at the source.
 */
export function stepTanks(current: string, delta: number): string {
  const parsed = Number(current);
  const base = Number.isFinite(parsed) && parsed > 0 ? parsed : MIN_TANKS;
  const next = round2(base + delta);
  return String(next < MIN_TANKS ? MIN_TANKS : next);
}

export interface ActualLine {
  productId: string;
  productName: string;
  quantity: number;
  unit: string;
  overridden: boolean;
}

/**
 * The per-product amounts, resolved ONCE per render for DISPLAY.
 *
 * Since #506 (D1) the server recomputes productsConsumed from
 * `tankCount x dosePerTank` and discards the client's array, so this feeds the
 * read-back line only — it is what the operator's tank count WILL book, not
 * what is submitted. The `overrides` map is retained in the signature because
 * the tests exercise it and it costs nothing, but production passes `{}`.
 */
export function resolveActuals(
  perTankMix: NutritionMixPreviewLine[],
  tanks: number,
  overrides: Record<string, number>,
): ActualLine[] {
  return (perTankMix || []).map((line) => {
    const pinned = overrides[line.productId];
    const overridden = typeof pinned === 'number' && Number.isFinite(pinned);
    return {
      productId: line.productId,
      productName: line.productName ?? line.productId,
      quantity: overridden ? pinned : round3((line.dosePerTank ?? 0) * tanks),
      unit: line.doseUnit,
      overridden,
    };
  });
}

/**
 * "Zinc 3.20 kg" per line — the read-back beneath the hero number.
 *
 * `formatQuantity`, NOT `formatMeasure` (Fix 2 — whole-branch review):
 * formatMeasure RESCALES to the largest family unit >= 1 (0.5 kg -> "500 g"),
 * which would put the read-back in a DIFFERENT unit than the one the
 * operator's tank count and the product's `doseUnit` are expressed in (a
 * sub-1 dose like 0.5 kg would read as "500 g"). The per-product override
 * editor this rule was originally written to protect (NutritionFoliarFieldPage.tsx)
 * was removed in #506 (D1) — the server now discards client quantities — but
 * the rule itself survives: the read-back is still the operator's only view
 * of what their tank count will book, and formatQuantity's fixed-2dp,
 * no-rescale formatting is what keeps that view in the SAME unit as the
 * plan (`doseUnit`) rather than a rescaled one the operator has to convert
 * in their head before trusting it.
 */
export function derivedReadbackLines(actuals: ActualLine[]): string[] {
  return actuals.map((l) => `${l.productName} ${formatQuantity(l.quantity, l.unit)}`);
}

/**
 * True for a cached work item that is NOT foliar.
 *
 * refreshMyNutritionSessions clears the Dexie table only on a SUCCESSFUL fetch,
 * so an operator who opens the app offline after this release still holds
 * yesterday's soil rows — which would render a card navigating to a page F6
 * deletes. An ABSENT kind is not a soil claim and is kept.
 */
export function isForeignWorkItem(item: { kind?: string }): boolean {
  return item.kind === 'soil';
}
